import { useEffect, useId, useMemo, useState } from 'react';
import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { useLookup, type LookupKind, type LookupOption } from './useLookup';
import {
  clearFilters,
  countActiveFilters,
  downloadReport,
  emailReport,
  FILTER_KEYS,
  formatShortDuration,
  reportLabel,
  type FilterKey,
  type ReportQueryState,
} from './report-utils';
import { TALK_TIME_BUCKETS } from '@marnic/shared';
import type { DailyReportingSettings, ReportFormat, ReportKind } from '@marnic/shared/reports';

interface Props {
  report: ReportKind;
  state: ReportQueryState;
  onChange: (next: ReportQueryState) => void;
  tabs?: boolean;
}

// A talk-time window is encoded as "min-max" seconds, either side blank for
// open-ended — the same shape the API takes, so a hand-written URL can express
// any range the dropdown doesn't offer.
const TALK_TIME_PRESETS = {
  atLeast: TALK_TIME_BUCKETS.filter((b) => b.minSeconds > 0).map((b) => ({
    value: `${b.minSeconds}-`,
    label: `${b.atLeastLabel} or longer`,
  })),
  // The open-ended top band duplicates its own "or longer" preset, so drop it.
  bands: TALK_TIME_BUCKETS.filter((b) => b.maxSeconds !== null).map((b) => ({
    value: `${b.minSeconds}-${b.maxSeconds}`,
    label: b.label,
  })),
};

function talkTimeValue(state: { minDuration: string; maxDuration: string }): string {
  if (!state.minDuration && !state.maxDuration) return '';
  return `${state.minDuration}-${state.maxDuration}`;
}

function talkTimePatch(value: string): { minDuration: string; maxDuration: string } {
  const [min = '', max = ''] = value.split('-');
  return { minDuration: min, maxDuration: max };
}

function isTalkTimePreset(value: string): boolean {
  if (!value) return true;
  return [...TALK_TIME_PRESETS.atLeast, ...TALK_TIME_PRESETS.bands].some((p) => p.value === value);
}

function describeTalkTime({ minDuration, maxDuration }: { minDuration: string; maxDuration: string }): string {
  const min = minDuration ? formatShortDuration(Number(minDuration)) : null;
  const max = maxDuration ? formatShortDuration(Number(maxDuration)) : null;
  if (min && max) return `${min} – ${max}`;
  if (min) return `${min} or longer`;
  return `under ${max}`;
}

const STATUSES: { value: string; label: string }[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'busy', label: 'Busy' },
  { value: 'failed', label: 'Failed' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'in_progress', label: 'In progress' },
];

export function ReportControls({ report, state, onChange, tabs = true }: Props): JSX.Element {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [dailyOpen, setDailyOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const role = useAuthStore((s) => s.user?.role);
  const isSupervisor = role === 'admin' || role === 'supervisor';

  const patchScalar = (value: Partial<ReportQueryState>): void => onChange({ ...state, ...value });
  const activeCount = countActiveFilters(state);

  async function runEmail(): Promise<void> {
    try {
      await emailReport(report, ['csv', 'pdf'], state);
      flash('Email report queued');
    } catch {
      flash('Could not queue email report', true);
    }
  }

  function flash(text: string, _isError = false): void {
    setMessage(text);
    setTimeout(() => setMessage(null), 2500);
  }

  function applyPreset(preset: DateRangePreset): void {
    const range = computePreset(preset);
    if (!range) return;
    onChange({ ...state, from: range.from, to: range.to });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <FieldGroup label="Date range">
          <input className="input w-36 py-1.5" type="date" value={state.from} onChange={(e) => patchScalar({ from: e.target.value })} aria-label="Date range from" />
          <span className="text-ink-400">–</span>
          <input className="input w-36 py-1.5" type="date" value={state.to} onChange={(e) => patchScalar({ to: e.target.value })} aria-label="Date range to" />
        </FieldGroup>
        <FieldGroup label="Quick range">
          <select
            className="input py-1.5"
            value=""
            onChange={(e) => {
              if (!e.target.value) return;
              applyPreset(e.target.value as DateRangePreset);
              e.currentTarget.value = '';
            }}
            aria-label="Quick date range"
          >
            <option value="">Select…</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 days</option>
            <option value="last30">Last 30 days</option>
            <option value="thisMonth">This month</option>
            <option value="lastMonth">Last month</option>
            <option value="last3Months">Last 3 months</option>
            <option value="yearToDate">Year to date</option>
            <option value="allTime">All time</option>
          </select>
        </FieldGroup>
        <FieldGroup label="Time range">
          <input className="input w-24 py-1.5" type="time" value={state.timeFrom} onChange={(e) => patchScalar({ timeFrom: e.target.value })} aria-label="Time range from" />
          <span className="text-ink-400">–</span>
          <input className="input w-24 py-1.5" type="time" value={state.timeTo === '24:00' ? '23:59' : state.timeTo} onChange={(e) => patchScalar({ timeTo: e.target.value })} aria-label="Time range to" />
        </FieldGroup>
        <button
          type="button"
          className="btn-secondary relative"
          onClick={() => setFiltersOpen(true)}
        >
          Filter
          {activeCount > 0 && (
            <span className="badge bg-brand-100 text-brand-700">{activeCount}</span>
          )}
        </button>
        <div className="ml-auto flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={() => void downloadReport(report, 'csv', state)}>CSV</button>
          <button type="button" className="btn-secondary" onClick={() => void downloadReport(report, 'pdf', state)}>PDF</button>
          <button type="button" className="btn-secondary" onClick={() => void runEmail()}>Email report</button>
          {isSupervisor && (
            <button type="button" className="btn-secondary" onClick={() => setDailyOpen(true)}>Daily reporting</button>
          )}
        </div>
      </div>

      {activeCount > 0 && (
        <ActiveFilterChips
          state={state}
          onClear={(key) => patchScalar({ [key]: '' } as Partial<ReportQueryState>)}
          onClearAll={() => onChange(clearFilters(state))}
        />
      )}

      {tabs && (
        <div className="flex justify-center gap-2">
          {(['total', 'inbound', 'outbound'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={`rounded-md px-4 py-1.5 text-sm font-semibold transition ${state.tab === tab ? 'bg-brand-100 text-brand-700' : 'text-ink-600 hover:bg-ink-100'}`}
              onClick={() => patchScalar({ tab })}
            >
              {tab === 'total' ? 'Total calls' : tab[0]!.toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      )}

      {message && <div className="text-sm text-emerald-700">{message}</div>}

      {filtersOpen && (
        <FilterModal
          initial={state}
          isSupervisor={isSupervisor}
          onApply={(next) => { onChange(next); setFiltersOpen(false); }}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      {dailyOpen && isSupervisor && (
        <DailyReportingModal
          report={report}
          onSaved={() => { setDailyOpen(false); flash('Daily reporting saved'); }}
          onClose={() => setDailyOpen(false)}
        />
      )}
    </div>
  );
}

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="label">{label}</div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

function ActiveFilterChips({ state, onClear, onClearAll }: { state: ReportQueryState; onClear: (key: FilterKey) => void; onClearAll: () => void }): JSX.Element {
  const agents = useLookup('agents');
  const campaigns = useLookup('campaigns');
  const queues = useLookup('queues');
  const ringGroups = useLookup('ringGroups');
  const numbers = useLookup('twilioNumbers');

  function resolveName(options: LookupOption[], id: string): string {
    return options.find((o) => o.id === id)?.name ?? id;
  }

  const labels: Record<FilterKey, string> = {
    phone: 'Phone',
    agentId: 'Agent',
    campaignId: 'Campaign',
    queueId: 'Queue',
    ringGroupId: 'Ring group',
    twilioNumberId: 'Number',
    direction: 'Direction',
    status: 'Status',
    hasRecording: 'Recording',
    minDuration: 'Talk time from',
    maxDuration: 'Talk time under',
  };

  const display = (key: FilterKey, value: string): string => {
    if (key === 'agentId') return resolveName(agents.options, value);
    if (key === 'campaignId') return resolveName(campaigns.options, value);
    if (key === 'queueId') return resolveName(queues.options, value);
    if (key === 'ringGroupId') return resolveName(ringGroups.options, value);
    if (key === 'twilioNumberId') return resolveName(numbers.options, value);
    if (key === 'direction') return value[0]!.toUpperCase() + value.slice(1);
    if (key === 'status') return STATUSES.find((s) => s.value === value)?.label ?? value;
    if (key === 'hasRecording') return value === 'true' ? 'With recording' : 'Without recording';
    if (key === 'minDuration' || key === 'maxDuration') return formatShortDuration(Number(value));
    return value;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {FILTER_KEYS.map((key) => {
        const value = state[key];
        if (!value) return null;
        return (
          <span key={key} className="inline-flex items-center gap-1 rounded border border-ink-200 bg-ink-50 px-2 py-0.5 text-[11px]">
            <span className="text-ink-500">{labels[key]}:</span>
            <span className="font-medium text-ink-700">{display(key, value)}</span>
            <button
              type="button"
              className="ml-1 leading-none text-ink-400 hover:text-ink-700"
              aria-label={`Remove ${labels[key]} filter`}
              onClick={() => onClear(key)}
            >
              ×
            </button>
          </span>
        );
      })}
      <button type="button" className="text-xs text-ink-500 underline-offset-2 hover:text-ink-700 hover:underline" onClick={onClearAll}>
        Clear all
      </button>
    </div>
  );
}

interface FilterModalProps {
  initial: ReportQueryState;
  isSupervisor: boolean;
  onApply: (next: ReportQueryState) => void;
  onClose: () => void;
}

function FilterModal({ initial, isSupervisor, onApply, onClose }: FilterModalProps): JSX.Element {
  const [draft, setDraft] = useState(initial);
  const titleId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patch = (value: Partial<ReportQueryState>): void => setDraft({ ...draft, ...value });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="card w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
          <h2 id={titleId} className="text-lg font-semibold">Filter calls</h2>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className="grid gap-3 p-4 sm:grid-cols-2">
          <Field label="Phone">
            <input className="input" placeholder="Any" value={draft.phone} onChange={(e) => patch({ phone: e.target.value })} />
          </Field>
          <Field label="Agent">
            <LookupSelect kind="agents" value={draft.agentId} onChange={(v) => patch({ agentId: v })} placeholder="Any agent" />
          </Field>
          <Field label="Campaign">
            <LookupSelect kind="campaigns" value={draft.campaignId} onChange={(v) => patch({ campaignId: v })} placeholder="Any campaign" />
          </Field>
          <Field label="Queue">
            {isSupervisor
              ? <LookupSelect kind="queues" value={draft.queueId} onChange={(v) => patch({ queueId: v })} placeholder="Any queue" />
              : <input className="input" placeholder="Queue ID" value={draft.queueId} onChange={(e) => patch({ queueId: e.target.value })} />}
          </Field>
          <Field label="Ring group">
            {isSupervisor
              ? <LookupSelect kind="ringGroups" value={draft.ringGroupId} onChange={(v) => patch({ ringGroupId: v })} placeholder="Any ring group" />
              : <input className="input" placeholder="Ring group ID" value={draft.ringGroupId} onChange={(e) => patch({ ringGroupId: e.target.value })} />}
          </Field>
          <Field label="DID / number">
            {isSupervisor
              ? <LookupSelect kind="twilioNumbers" value={draft.twilioNumberId} onChange={(v) => patch({ twilioNumberId: v })} placeholder="Any number" />
              : <input className="input" placeholder="Number ID" value={draft.twilioNumberId} onChange={(e) => patch({ twilioNumberId: e.target.value })} />}
          </Field>
          <Field label="Direction">
            <select className="input" value={draft.direction} onChange={(e) => patch({ direction: e.target.value })}>
              <option value="">Any</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </Field>
          <Field label="Status">
            <select className="input" value={draft.status} onChange={(e) => patch({ status: e.target.value })}>
              <option value="">Any</option>
              {STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
          </Field>
          <Field label="Recording">
            <select className="input" value={draft.hasRecording} onChange={(e) => patch({ hasRecording: e.target.value })}>
              <option value="">Any</option>
              <option value="true">With recording</option>
              <option value="false">Without recording</option>
            </select>
          </Field>
          <Field label="Talk time">
            <select className="input" value={talkTimeValue(draft)} onChange={(e) => patch(talkTimePatch(e.target.value))}>
              <option value="">Any</option>
              {!isTalkTimePreset(talkTimeValue(draft)) && (
                // Keeps a hand-entered range from making the select read "Any".
                <option value={talkTimeValue(draft)}>
                  Custom: {describeTalkTime(draft)}
                </option>
              )}
              <optgroup label="At least">
                {TALK_TIME_PRESETS.atLeast.map((preset) => (
                  <option key={preset.value} value={preset.value}>{preset.label}</option>
                ))}
              </optgroup>
              <optgroup label="Exactly">
                {TALK_TIME_PRESETS.bands.map((preset) => (
                  <option key={preset.value} value={preset.value}>{preset.label}</option>
                ))}
              </optgroup>
            </select>
          </Field>
          <FieldSet label="Custom talk time (seconds)">
            <div className="flex items-center gap-2">
              <input
                className="input"
                type="number"
                min="0"
                placeholder="Min"
                aria-label="Minimum talk time in seconds"
                value={draft.minDuration}
                onChange={(e) => patch({ minDuration: e.target.value })}
              />
              <span className="text-ink-400">–</span>
              <input
                className="input"
                type="number"
                min="0"
                placeholder="Max"
                aria-label="Maximum talk time in seconds"
                value={draft.maxDuration}
                onChange={(e) => patch({ maxDuration: e.target.value })}
              />
            </div>
          </FieldSet>
        </div>
        <footer className="flex items-center justify-between gap-2 border-t border-ink-200 bg-ink-50 px-4 py-3">
          <button type="button" className="btn-ghost text-sm" onClick={() => setDraft(clearFilters(draft))}>Reset filters</button>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => onApply(draft)}>Apply</button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function LookupSelect({ kind, value, onChange, placeholder }: { kind: LookupKind; value: string; onChange: (v: string) => void; placeholder: string }): JSX.Element {
  const { options, isLoading } = useLookup(kind);
  return (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)} disabled={isLoading}>
      <option value="">{isLoading ? 'Loading…' : placeholder}</option>
      {options.map((option) => (
        <option key={option.id} value={option.id}>{option.name}</option>
      ))}
    </select>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return <label className="block"><span className="label">{label}</span>{children}</label>;
}

/** Like Field, but for groups of more than one control — a label can only name one. */
function FieldSet({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return <div><span className="label">{label}</span>{children}</div>;
}

interface DailyReportingModalProps {
  report: ReportKind;
  onSaved: () => void;
  onClose: () => void;
}

interface FtpSettings {
  host: string | null;
  username: string | null;
  enabled: boolean;
}

function DailyReportingModal({ report, onSaved, onClose }: DailyReportingModalProps): JSX.Element {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const settingsQuery = useQuery({
    queryKey: ['daily-reporting'],
    queryFn: async () => (await api.get<{ data: DailyReportingSettings[] }>('/reports/daily-reporting')).data,
  });
  const ftpQuery = useQuery({
    queryKey: ['daily-reporting-ftp'],
    queryFn: () => api.get<FtpSettings>('/reports/daily-reporting-ftp-settings'),
  });

  const existing = useMemo(
    () => settingsQuery.data?.find((s) => s.report === report),
    [settingsQuery.data, report],
  );

  const [enabled, setEnabled] = useState(true);
  const [delivery, setDelivery] = useState<'email' | 'ftp'>('email');
  const [recipientInput, setRecipientInput] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);
  const [formats, setFormats] = useState<ReportFormat[]>(['csv']);
  const [ftpHost, setFtpHost] = useState('');
  const [ftpUsername, setFtpUsername] = useState('');
  const [ftpPassword, setFtpPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (hydrated) return;
    if (settingsQuery.isLoading || ftpQuery.isLoading) return;
    if (existing) {
      setEnabled(existing.enabled);
      setDelivery(existing.delivery);
      setRecipients(existing.recipients);
      setFormats(existing.formats.length > 0 ? existing.formats : ['csv']);
      setFtpHost(existing.ftpHost ?? ftpQuery.data?.host ?? '');
      setFtpUsername(existing.ftpUsername ?? ftpQuery.data?.username ?? '');
    } else {
      setFtpHost(ftpQuery.data?.host ?? '');
      setFtpUsername(ftpQuery.data?.username ?? '');
    }
    setHydrated(true);
  }, [hydrated, existing, settingsQuery.isLoading, ftpQuery.isLoading, ftpQuery.data]);

  function addRecipient(): void {
    const trimmed = recipientInput.trim();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Enter a valid email address');
      return;
    }
    if (recipients.includes(trimmed)) {
      setError('That recipient is already in the list');
      return;
    }
    setRecipients([...recipients, trimmed]);
    setRecipientInput('');
    setError(null);
  }

  function toggleFormat(format: ReportFormat): void {
    setFormats((current) =>
      current.includes(format) ? current.filter((f) => f !== format) : [...current, format],
    );
  }

  async function regeneratePassword(): Promise<void> {
    try {
      const result = await api.post<{ newPassword: string }>('/reports/daily-reporting-ftp-settings/regenerate-password');
      setFtpPassword(result.newPassword);
      setError(null);
    } catch {
      setError('Could not regenerate password');
    }
  }

  async function save(): Promise<void> {
    if (delivery === 'email' && enabled && recipients.length === 0) {
      setError('Add at least one email recipient');
      return;
    }
    if (formats.length === 0) {
      setError('Pick at least one format');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put('/reports/daily-reporting', {
        report,
        enabled,
        recipients,
        formats,
        delivery,
        ftpHost: delivery === 'ftp' ? ftpHost.trim() || null : null,
        ftpUsername: delivery === 'ftp' ? ftpUsername.trim() || null : null,
      });
      onSaved();
    } catch {
      setError('Could not save daily reporting settings');
    } finally {
      setSaving(false);
    }
  }

  const loading = !hydrated && (settingsQuery.isLoading || ftpQuery.isLoading);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="card w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
          <div>
            <h2 id={titleId} className="text-lg font-semibold">Daily reporting</h2>
            <p className="text-xs text-ink-500">{reportLabel(report)}</p>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">×</button>
        </header>

        {loading ? (
          <div className="space-y-3 p-4">
            <div className="h-4 w-full animate-pulse rounded bg-ink-100" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-ink-100" />
          </div>
        ) : (
          <div className="space-y-4 p-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <span>Send daily report</span>
            </label>

            <div>
              <div className="label">Delivery</div>
              <div className="flex gap-2">
                <DeliveryOption active={delivery === 'email'} onClick={() => setDelivery('email')} label="Email" />
                <DeliveryOption active={delivery === 'ftp'} onClick={() => setDelivery('ftp')} label="FTP" />
              </div>
            </div>

            {delivery === 'email' && (
              <div>
                <div className="label">Recipients</div>
                <div className="flex flex-wrap gap-1.5">
                  {recipients.map((email) => (
                    <span key={email} className="inline-flex items-center gap-1 rounded border border-ink-200 bg-ink-50 px-2 py-0.5 text-xs">
                      {email}
                      <button type="button" className="text-ink-400 hover:text-ink-700" onClick={() => setRecipients(recipients.filter((r) => r !== email))} aria-label={`Remove ${email}`}>×</button>
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <input
                    className="input flex-1"
                    type="email"
                    placeholder="recipient@example.com"
                    value={recipientInput}
                    onChange={(e) => setRecipientInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
                  />
                  <button type="button" className="btn-secondary" onClick={addRecipient}>Add</button>
                </div>
              </div>
            )}

            {delivery === 'ftp' && (
              <div className="space-y-2">
                <Field label="FTP host">
                  <input className="input" placeholder="reports.example.com" value={ftpHost} onChange={(e) => setFtpHost(e.target.value)} />
                </Field>
                <Field label="FTP username">
                  <input className="input" placeholder="username" value={ftpUsername} onChange={(e) => setFtpUsername(e.target.value)} />
                </Field>
                <div>
                  <div className="label">FTP password</div>
                  <div className="flex items-center gap-2">
                    <input className="input flex-1 font-mono" readOnly placeholder="•••••••• (regenerate to view)" value={ftpPassword ?? ''} />
                    <button type="button" className="btn-secondary" onClick={() => void regeneratePassword()}>Regenerate</button>
                  </div>
                  {ftpPassword && (
                    <p className="mt-1 text-xs text-ink-500">Copy this password now — it won't be shown again.</p>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="label">Formats</div>
              <div className="flex gap-3 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={formats.includes('csv')} onChange={() => toggleFormat('csv')} />
                  CSV
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={formats.includes('pdf')} onChange={() => toggleFormat('pdf')} />
                  PDF
                </label>
              </div>
            </div>

            {error && <div className="text-sm text-red-600">{error}</div>}
          </div>
        )}

        <footer className="flex items-center justify-end gap-2 border-t border-ink-200 bg-ink-50 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => void save()} disabled={loading || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

type DateRangePreset =
  | 'today' | 'yesterday' | 'last7' | 'last30'
  | 'thisMonth' | 'lastMonth' | 'last3Months'
  | 'yearToDate' | 'allTime';

function computePreset(preset: DateRangePreset): { from: string; to: string } | null {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const iso = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  switch (preset) {
    case 'today':
      return { from: iso(today), to: iso(today) };
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { from: iso(y), to: iso(y) };
    }
    case 'last7': {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from: iso(from), to: iso(today) };
    }
    case 'last30': {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { from: iso(from), to: iso(today) };
    }
    case 'thisMonth': {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: iso(from), to: iso(today) };
    }
    case 'lastMonth': {
      const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const to = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: iso(from), to: iso(to) };
    }
    case 'last3Months': {
      const from = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      return { from: iso(from), to: iso(today) };
    }
    case 'yearToDate': {
      const from = new Date(today.getFullYear(), 0, 1);
      return { from: iso(from), to: iso(today) };
    }
    case 'allTime':
      return { from: '2020-01-01', to: iso(today) };
    default:
      return null;
  }
}

function DeliveryOption({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition ${
        active
          ? 'border-brand-500 bg-brand-50 text-brand-700'
          : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'
      }`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
